import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  Link2,
  Linkedin,
  MessageCircle,
  Globe,
} from "lucide-react";
import FIELD_NOTES from "../content/fieldNotes/generated";
import { IMAGES } from "../mock";
import { getLang } from "../i18n";
import { Ornament } from "../components/bits";
import VideoBackground from "../components/VideoBackground";

const OG_IMAGE = IMAGES.cosmicLight;

const T = {
  en: {
    back: "Back to Field Notes",
    share: "Share",
    author: "Author",
    role: "Attuning Companion, Nonlocal Field Harmonization",
    previous: "Previous",
    next: "Next",
    closing: "If this resonated, you are warmly welcome to the free Sunday Common Session.",
    notFound: "Field Note not found",
    notFoundBody: "This reflection may have moved or been renamed.",
    copied: "Link copied to clipboard",
    copyError: "Could not copy the link.",
    readOther: "Auf Deutsch lesen",
    locale: "en-US",
  },
  de: {
    back: "Zurück zu den Field Notes",
    share: "Teilen",
    author: "Autor",
    role: "Einstimmender Begleiter, Nichtlokale Feldharmonisierung",
    previous: "Zurück",
    next: "Weiter",
    closing: "Wenn dich das berührt hat, bist du herzlich zur kostenlosen Sonntagssitzung eingeladen.",
    notFound: "Field Note nicht gefunden",
    notFoundBody: "Diese Reflexion wurde möglicherweise verschoben oder umbenannt.",
    copied: "Link in die Zwischenablage kopiert",
    copyError: "Der Link konnte nicht kopiert werden.",
    readOther: "Read in English",
    locale: "de-DE",
  },
};

const XIcon = (props) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const FieldNoteArticle = () => {
  const { slug } = useParams();
  const location = useLocation();
  const lang = getLang(location.pathname);
  const t = T[lang];
  const basePath = lang === "de" ? "/de/field-notes" : "/field-notes";
  const contactsPath = lang === "de" ? "/de/kontakt" : "/contacts";
  const [progress, setProgress] = useState(0);

  const note = FIELD_NOTES.find((n) => n.slug === slug) || null;

  // Same-language ordered list for prev/next.
  const langNotes = useMemo(
    () => FIELD_NOTES.filter((n) => n.lang === (note ? note.lang : lang)),
    [note, lang]
  );
  const byIssue = (i) => (note ? langNotes.find((n) => n.issue === i) || null : null);
  const newer = note ? byIssue(note.issue - 1) : null;
  const older = note ? byIssue(note.issue + 1) : null;

  const translation = useMemo(() => {
    if (!note || !note.translationSlug) return null;
    return FIELD_NOTES.find((n) => n.slug === note.translationSlug) || null;
  }, [note]);

  const fmtDate = (d) =>
    new Date(d).toLocaleDateString(t.locale, { year: "numeric", month: "long", day: "numeric" });

  useEffect(() => {
    if (!note) return;
    document.title = `${note.title} — Field Notes · NLFH`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", note.excerpt || note.subtitle || "");
  }, [note]);

  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const total = el.scrollHeight - el.clientHeight;
      setProgress(total > 0 ? Math.min(100, (el.scrollTop / total) * 100) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [slug]);

  if (!note) {
    return (
      <main className="video-page">
        <VideoBackground veil={0.5} />
        <section className="section" style={{ paddingTop: "10rem" }}>
          <div className="container-page container-narrow" style={{ textAlign: "center" }}>
            <h1 className="h-section">{t.notFound}</h1>
            <p className="lead" style={{ marginTop: "1rem" }}>
              {t.notFoundBody}
            </p>
            <Link to={basePath} className="btn-gold" style={{ marginTop: "1.75rem" }}>
              <ArrowLeft size={16} /> {t.back}
            </Link>
          </div>
        </section>
      </main>
    );
  }

  const shareUrl = `${window.location.origin}${location.pathname}`;
  const shareText = `Reading "${note.title}" by Dr. Luca Pizzimbone on nonlocalfield.io`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t.copied);
    } catch {
      toast.error(t.copyError);
    }
  };

  const shareLinks = {
    x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`,
  };

  const translationPath = translation ? `${basePath === "/de/field-notes" ? "/field-notes" : "/de/field-notes"}/${translation.slug}` : null;

  return (
    <main className="video-page" data-testid="field-note-article">
      <div className="fn-progress" style={{ width: `${progress}%` }} data-testid="reading-progress-bar" />
      <VideoBackground veil={0.55} />

      <article className="fn-article">
        <div className="container-page container-narrow">
          <Link to={basePath} className="fn-back" data-testid="back-to-field-notes">
            <ArrowLeft size={16} /> {t.back}
          </Link>

          <header className="fn-article-head">
            <div className="fn-article-meta-top">
              <span className="fn-cat">{note.category}</span>
              {translation && (
                <Link to={translationPath} className="fn-lang-toggle" data-testid="field-note-lang-toggle">
                  <Globe size={14} /> {t.readOther}
                </Link>
              )}
            </div>
            <h1 className="fn-article-title">{note.title}</h1>
            <p className="fn-article-sub">{note.subtitle}</p>
            <div className="fn-article-meta">
              <span>{fmtDate(note.date)}</span>
              <span className="fn-dot">·</span>
              <span className="fn-reading"><Clock size={13} /> {note.readingTime}</span>
            </div>

            <div className="fn-share" data-testid="share-toolbar">
              <span className="fn-share-label">{t.share}</span>
              <button type="button" onClick={copyLink} className="fn-share-btn" aria-label="Copy link" data-testid="share-copy-link">
                <Link2 size={16} />
              </button>
              <a href={shareLinks.x} target="_blank" rel="noopener noreferrer" className="fn-share-btn" aria-label="Share on X" data-testid="share-x">
                <XIcon />
              </a>
              <a href={shareLinks.linkedin} target="_blank" rel="noopener noreferrer" className="fn-share-btn" aria-label="Share on LinkedIn" data-testid="share-linkedin">
                <Linkedin size={16} />
              </a>
              <a href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer" className="fn-share-btn" aria-label="Share on WhatsApp" data-testid="share-whatsapp">
                <MessageCircle size={16} />
              </a>
            </div>
          </header>

          <div className="fn-prose" data-testid="field-note-body">
            <ReactMarkdown>{note.body}</ReactMarkdown>
          </div>

          <footer className="fn-article-footer">
            <Ornament style={{ margin: "0 0 1.75rem" }} />
            <div className="fn-author">
              <span className="eyebrow" style={{ color: "var(--gold-strong)" }}>{t.author}</span>
              <p className="fn-author-name">Dr. Ing. Luca Pizzimbone</p>
              <p className="fn-author-role">{t.role}</p>
            </div>

            <div className="fn-prevnext">
              {newer ? (
                <Link to={`${basePath}/${newer.slug}`} className="fn-prevnext-link" data-testid="prev-field-note">
                  <span className="fn-prevnext-dir"><ArrowLeft size={14} /> {t.previous}</span>
                  <span className="fn-prevnext-title">{newer.title}</span>
                </Link>
              ) : (
                <span className="fn-prevnext-link empty" aria-hidden="true" />
              )}
              {older ? (
                <Link to={`${basePath}/${older.slug}`} className="fn-prevnext-link right" data-testid="next-field-note">
                  <span className="fn-prevnext-dir">{t.next} <ArrowRight size={14} /></span>
                  <span className="fn-prevnext-title">{older.title}</span>
                </Link>
              ) : (
                <span className="fn-prevnext-link empty" aria-hidden="true" />
              )}
            </div>

            <div className="fn-closing">
              <p>{t.closing}</p>
              <Link to={contactsPath} className="btn-ghost" data-testid="field-note-closing-cta">
                nonlocalfield.io/contacts
              </Link>
            </div>
          </footer>
        </div>
      </article>

      <meta property="og:image" content={OG_IMAGE} />
    </main>
  );
};

export default FieldNoteArticle;
